import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listInquiries, inquiriesExportUrl } from "../lib/architectPortal";
import ArchitectPortalSubnav from "../components/ArchitectPortalSubnav";
import { assetUrl } from "../lib/format";
import {
  Download,
  Mail,
  Search,
  Building2,
  User,
  AlertCircle,
  ArrowUpRight,
} from "lucide-react";
import StatusPill from "../components/StatusPill";
import { buildFilterBar } from "../lib/statusFilters";

const FILTERS = buildFilterBar([
  "Consultation Requested",
  "In Consultation",
  "Activated",
  "Declined",
]);

const ROW_LABEL_OVERRIDE = {
  "Consultation Requested": "NEW",
};

const FILTER_EMPTY_COPY = {
  "Consultation Requested": "No new inquiries.",
  "In Consultation": "No inquiries in active consultation.",
  Activated: "No activated inquiries yet.",
  Declined: "No declined inquiries.",
  all: "Your inbox is empty.",
};

export default function InquiryInbox() {
  const { user } = useRole();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    setData(null);
    listInquiries(user.id, { status: filter, q: debouncedQ })
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id, filter, debouncedQ]);

  const hasResults = data && data.items.length > 0;
  const isSearching = debouncedQ.length > 0;

  return (
    <section className="animate-fade-in-up" data-testid="inquiry-inbox-page">
      <ArchitectPortalSubnav />

      {/* ── Header (matches dashboard's calmer scale) ─────────────── */}
      <div
        className="flex items-end justify-between flex-wrap"
        style={{ gap: 24, paddingTop: 8, paddingBottom: 28 }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 22,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Inbox
            </span>
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(24px, 3.2vw, 34px)",
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
              margin: "12px 0 0",
            }}
            data-testid="inquiry-inbox-h1"
          >
            Consultation inquiries.
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
              lineHeight: 1.55,
              marginTop: 8,
            }}
          >
            {data ? summaryLine(data, filter, isSearching, debouncedQ) : " "}
          </div>
        </div>
        {user?.id && (
          <a
            className="wn-btn wn-btn-tertiary"
            href={inquiriesExportUrl(user.id)}
            data-testid="inbox-export-csv"
          >
            <Download size={14} />
            Export CSV
          </a>
        )}
      </div>

      {/* ── Filter chips + search ─────────────────────────────────── */}
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <div className="flex flex-wrap gap-2" data-testid="inbox-filters">
          {FILTERS.map((f) => {
            const isOn = filter === f.key;
            const count =
              f.key === "all"
                ? data?.total
                : data?.counts?.[f.key];
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`inbox-filter-${f.key.replace(/\s+/g, "_")}`}
                style={pillStyle(isOn)}
              >
                {f.label}
                {count !== undefined && (
                  <span
                    className="tabular-nums"
                    style={{ marginLeft: 6, opacity: 0.7 }}
                  >
                    · {count || 0}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div
          className="flex items-center gap-2 wn-card"
          style={{
            padding: "0 12px",
            height: 34,
            minWidth: 280,
            marginLeft: "auto",
          }}
        >
          <Search size={14} style={{ color: "#9AA0C0" }} />
          <input
            data-testid="inbox-search"
            placeholder="Search address, client, or design…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              border: 0,
              outline: 0,
              fontSize: 13,
              background: "transparent",
              width: "100%",
              fontFamily: '"Nunito Sans", sans-serif',
              color: "#272636",
            }}
          />
        </div>
      </div>

      {/* ── Results ──────────────────────────────────────────────── */}
      {err && (
        <p
          className="mt-8"
          style={{ color: "#A23B2E" }}
          data-testid="inbox-error"
        >
          Could not load your inbox.
        </p>
      )}

      {!data && !err && (
        <div className="mt-6 space-y-2" data-testid="inbox-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="shimmer"
              style={{ height: 100, borderRadius: 8 }}
            />
          ))}
        </div>
      )}

      {data && !hasResults && (
        <EmptyStateCard
          filter={filter}
          isSearching={isSearching}
          searchTerm={debouncedQ}
        />
      )}

      {hasResults && (
        <div className="mt-5 space-y-2" data-testid="inbox-list">
          {data.items.map((c) => (
            <InquiryRow
              key={c.id}
              consult={c}
              onOpen={() => navigate(`/architect/inquiries/${c.id}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
function summaryLine(data, filter, isSearching, q) {
  if (isSearching) {
    const n = data.items.length;
    return n === 0
      ? `No matches for "${q}".`
      : `${n} match${n === 1 ? "" : "es"} for "${q}".`;
  }
  if (filter !== "all") {
    const label = FILTERS.find((f) => f.key === filter)?.label || filter;
    return `Filtered to ${label}.`;
  }
  const total = data.total || 0;
  const newCount = data.counts?.["Consultation Requested"] || 0;
  if (total === 0) return "No inquiries yet.";
  return `${total} inquir${total === 1 ? "y" : "ies"} on file · ${newCount} new.`;
}

function pillStyle(isOn) {
  return {
    padding: "6px 13px",
    borderRadius: 999,
    border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
    background: isOn ? "#E6E7F1" : "#FBFAFD",
    color: isOn ? "#1B1F4B" : "#272636",
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: isOn ? 700 : 600,
    fontSize: 11.5,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    cursor: "pointer",
    lineHeight: 1,
  };
}

function EmptyStateCard({ filter, isSearching, searchTerm }) {
  const copy = isSearching
    ? `Nothing matches "${searchTerm}".`
    : FILTER_EMPTY_COPY[filter] || FILTER_EMPTY_COPY.all;
  return (
    <div
      className="mt-8 wn-card"
      style={{ padding: "32px 24px", textAlign: "center" }}
      data-testid="inbox-empty"
    >
      <Mail
        size={32}
        style={{ color: "#9AA0C0", margin: "0 auto" }}
      />
      <h3
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 19,
          color: "#1B1F4B",
        }}
      >
        {copy}
      </h3>
      {!isSearching && filter === "all" && (
        <p
          className="mt-2"
          style={{ color: "#6C6B85", fontSize: 13.5, lineHeight: 1.55 }}
        >
          Work usually arrives through your{" "}
          <Link
            to="/architects"
            style={{ color: "#1B1F4B", fontWeight: 700 }}
          >
            public profile
          </Link>
          .
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function InquiryRow({ consult, onOpen }) {
  const photo = consult.design?.photo
    ? assetUrl(consult.design.photo)
    : consult.design?.exterior_photo
      ? assetUrl(consult.design.exterior_photo)
      : null;
  const owner = useMemo(() => ownerLines(consult.owner), [consult.owner]);
  const typology = humanTypology(consult.design?.typology);
  const stale = staleLabel(consult);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="wn-card text-left transition-all"
      style={{
        width: "100%",
        padding: "18px 20px",
        display: "grid",
        // Fixed widths on the trailing columns guarantee visual alignment
        // across every row regardless of pill / date string length.
        gridTemplateColumns:
          "minmax(0, 1.5fr) minmax(0, 1.1fr) minmax(0, 1.1fr) 140px 96px",
        alignItems: "start",
        gap: 20,
        cursor: "pointer",
      }}
      data-testid={`inbox-row-${consult.id}`}
    >
      {/* Owner */}
      <div className="min-w-0">
        <div className="flex items-center" style={{ gap: 6 }}>
          {owner.isOrg ? (
            <Building2 size={13} style={{ color: "#6C6B85", flexShrink: 0 }} />
          ) : (
            <User size={13} style={{ color: "#6C6B85", flexShrink: 0 }} />
          )}
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 16,
              color: "#1B1F4B",
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {owner.primary}
          </div>
        </div>
        {owner.secondary && (
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12.5,
              marginTop: 2,
              marginLeft: 19,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {owner.secondary}
          </div>
        )}
        {consult.first_message && (
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#8F8DA5",
              fontSize: 12.5,
              marginTop: 6,
              marginLeft: 19,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            data-testid={`inbox-preview-${consult.id}`}
          >
            “{consult.first_message}”
          </div>
        )}
      </div>

      {/* Parcel */}
      <div className="min-w-0" style={{ paddingTop: 2 }}>
        <div
          style={{
            color: "#272636",
            fontSize: 13.5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 600,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {consult.parcel?.address || "—"}
        </div>
        <div
          style={{
            color: "#6C6B85",
            fontSize: 12,
            marginTop: 2,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {consult.parcel?.neighborhood || ""}
        </div>
      </div>

      {/* Design */}
      <div className="flex items-center min-w-0" style={{ gap: 12 }}>
        <span
          aria-hidden
          style={{
            width: 52,
            height: 52,
            borderRadius: 6,
            backgroundImage: photo ? `url(${photo})` : "none",
            backgroundColor: photo ? "transparent" : "#ECEBF1",
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexShrink: 0,
            border: "1px solid #D9D8E4",
          }}
        />
        <div className="min-w-0">
          <div
            style={{
              color: "#1B1F4B",
              fontSize: 13.5,
              fontWeight: 700,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: '"Nunito Sans", sans-serif',
            }}
          >
            {consult.design?.name || "—"}
          </div>
          {typology && (
            <div
              style={{
                color: "#6C6B85",
                fontSize: 12,
                marginTop: 2,
                fontFamily: '"Nunito Sans", sans-serif',
              }}
            >
              {typology}
            </div>
          )}
        </div>
      </div>

      {/* Status (+ STALE chip beneath when applicable) */}
      <div
        className="flex flex-col items-end"
        style={{ gap: 6, paddingTop: 2 }}
      >
        <StatusPill
          value={consult.status}
          label={ROW_LABEL_OVERRIDE[consult.status]}
          testId={`inbox-state-${consult.status.replace(/\s+/g, "_")}`}
        />
        {stale && (
          <span
            className="inline-flex items-center"
            style={{
              gap: 4,
              padding: "2px 8px",
              background: "#FAF6EC",
              border: "1px solid #E4D5B5",
              borderRadius: 999,
              color: "#8a651f",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
            data-testid={`inbox-stale-${consult.id}`}
          >
            <AlertCircle size={10} strokeWidth={2.4} />
            {stale}
          </span>
        )}
      </div>

      {/* Time + open affordance */}
      <div
        className="flex items-center"
        style={{
          gap: 8,
          justifyContent: "flex-end",
          paddingTop: 4,
        }}
      >
        <span
          className="tabular-nums"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
            whiteSpace: "nowrap",
            textAlign: "right",
          }}
        >
          {relativeShort(consult.opened_at)}
        </span>
        <ArrowUpRight
          size={14}
          style={{ color: "#9AA0C0", flexShrink: 0 }}
        />
      </div>
    </button>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
/* Owner display logic — never duplicate the org line under itself.
   - Organization: primary line is the org; secondary is the contact name
     (only when both exist AND aren't substrings of each other).
   - Individual: primary is the person name; no secondary line. */
function ownerLines(owner) {
  if (!owner) return { primary: "—", secondary: null, isOrg: false };
  const name = (owner.name || "").trim();
  const org = (owner.organization || "").trim();
  const isOrg = !!org;
  if (!isOrg) return { primary: name || "—", secondary: null, isOrg: false };
  // Org case — pick the longer of the two as primary (it carries the (CDC) suffix).
  const [primary, secondary] = org.length >= name.length
    ? [org, name]
    : [name, org];
  // If they overlap meaningfully, don't repeat.
  const same =
    !secondary ||
    primary.toLowerCase().includes(secondary.toLowerCase()) ||
    secondary.toLowerCase().includes(primary.toLowerCase());
  return {
    primary,
    secondary: same ? null : secondary,
    isOrg: true,
  };
}

function humanTypology(t) {
  if (!t) return null;
  const map = {
    single_family: "Single family",
    multi_family: "Multi-family",
    adu: "ADU",
    duplex: "Duplex",
    townhouse: "Townhouse",
    cottage: "Cottage",
    studio: "Studio",
  };
  return map[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* Stale chip — "Consultation Requested" rows older than 30 days. */
function staleLabel(consult) {
  if (consult.status !== "Consultation Requested") return null;
  if (!consult.opened_at) return null;
  const days = Math.floor(
    (Date.now() - new Date(consult.opened_at).getTime()) / 86400000,
  );
  if (days < 30) return null;
  return days < 60
    ? `Stale · ${days} days`
    : `Stale · ${Math.floor(days / 7)} weeks`;
}

/* Relative date — short form. Uses year qualifier for entries > 90d old
   so "Dec 16" never reads ambiguously across years. */
function relativeShort(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const d = new Date(iso);
  if (days < 90) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
